// Idle auto-end (spec-v2.1.md, "Mic and calls"): after 3 minutes with no speech or typing from the user, no running
// jobs and no playback, the web asks the Director to say it's going quiet, then ends the call after that line. One
// tap reconnects, and the carryover keeps the context. Pure; the controller (src/idleController.ts) runs it on a
// clock. Unit-tested in idle.test.ts.

export const IDLE_MS = 3 * 60_000;
/** After asking the Director to sign off, end anyway if its line hasn't come and gone in this long. */
export const GOODBYE_MS = 12_000;

export type IdleInput = {
  now: number;
  connected: boolean;
  /** the user's last speech, typing or tap */
  lastActivity: number;
  /** jobs running or something playing right now */
  busy: boolean;
  /** the last moment something was busy (a job or playback ending restarts the clock) */
  lastBusy: number;
  /** when the web asked the Director to sign off, or null */
  warnedAt: number | null;
  directorSpeaking: boolean;
  /** the Director has spoken since the warning (its goodbye line) */
  spokeSinceWarn: boolean;
};

export type IdleAction = "none" | "warn" | "end" | "cancel";

export function idleStep(s: IdleInput, limitMs = IDLE_MS, goodbyeMs = GOODBYE_MS): IdleAction {
  if (!s.connected) return s.warnedAt !== null ? "cancel" : "none";
  if (s.warnedAt !== null) {
    // the user came back, or work started: keep the call
    if (s.lastActivity > s.warnedAt || s.busy) return "cancel";
    if (s.directorSpeaking) return "none";
    if (s.spokeSinceWarn || s.now - s.warnedAt >= goodbyeMs) return "end";
    return "none";
  }
  if (s.busy || s.directorSpeaking) return "none";
  const quietSince = Math.max(s.lastActivity, s.lastBusy);
  return s.now - quietSince >= limitMs ? "warn" : "none";
}

/** What the web tells the Director when it's time to sign off. */
export const IDLE_WARNING =
  "[app] It's been quiet for three minutes and nothing is running. Say one short line that you're going quiet to save the line and that a tap brings you back (for example \"I'll hang up for now. Tap me when you need me.\"), then stop talking. The app ends the call after your line.";
